import React, { useEffect, useState, useRef, useCallback } from "react";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
import { Upload, Trash2, Copy, Loader2, Film, Check, Link2 } from "lucide-react";
import { toast } from "sonner";
import useGoogleDrivePicker from "@/lib/useGoogleDrivePicker";
import { useT } from "@/lib/LanguageContext";
// Relative on purpose: no "@" alias on the Jest side (cf. LanguageAwareField.jsx).
import useFieldErrors from "../hooks/useFieldErrors";
import { requiredField } from "../lib/validation";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import { cn } from "../lib/utils";

const CHUNK = 5 * 1024 * 1024; // 5 Mo / chunk (contourne la limite du proxy)
const CHUNK_THRESHOLD = 5 * 1024 * 1024;

// The URL-import box. The server fetches whatever is posted here, so a required, bounded
// value is the least the form owes it; the shape of the URL itself is the server's call
// (it is the party that dereferences it) and its refusal reaches the toast through
// extractApiError.
const importUrlSchema = z.object({ importUrl: requiredField(2000) });

export default function MediaAdmin() {
  const t = useT("admin");
  const tv = useT("common");
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [copiedId, setCopiedId] = useState(null);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const { configured: driveConfigured, ready: driveReady, openPicker } = useGoogleDrivePicker();

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/media");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const importFromDrive = () => {
    openPicker(
      async ({ file_id, filename, access_token }) => {
        setImporting(true);
        try {
          await api.post("/admin/media/drive-import", { file_id, filename, access_token });
          toast.success(t("media.toast.driveImported", { name: filename || t("media.file") }));
          await load();
        } catch (e) {
          toast.error(extractApiError(e, t("media.toast.driveImportFailed"), tv));
        } finally {
          setImporting(false);
        }
      },
      (err) => toast.error(err?.message || t("media.toast.driveConnectFailed")),
    );
  };

  const importFromUrl = async () => {
    // Judged ON CLICK rather than by disabling the button (house rule): a dead button
    // states no reason, and the empty case used to `return` in silence.
    if (validate(importUrlSchema, { importUrl })) { toast.error(tv("validation.fixHighlighted")); return; }
    setImporting(true);
    try {
      await api.post("/admin/media/import-url", { url: importUrl.trim() });
      toast.success(t("media.toast.urlImported"));
      setImportUrl("");
      await load();
    } catch (e) {
      toast.error(extractApiError(e, t("media.toast.urlImportFailed"), tv));
    } finally {
      setImporting(false);
    }
  };

  const uploadSingle = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    await api.post("/admin/media", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => e.total && setProgress(Math.round((100 * e.loaded) / e.total)),
    });
  };

  const uploadChunked = async (file) => {
    const { data } = await api.post("/admin/media/chunk/init", { filename: file.name, content_type: file.type });
    const uploadId = data.upload_id;
    let sent = 0;
    for (let off = 0; off < file.size; off += CHUNK) {
      const blob = file.slice(off, off + CHUNK);
      // eslint-disable-next-line no-await-in-loop
      await api.put(`/admin/media/chunk/${uploadId}`, blob, { headers: { "Content-Type": "application/octet-stream" } });
      sent += blob.size;
      setProgress(Math.round((100 * sent) / file.size));
    }
    await api.post(`/admin/media/chunk/${uploadId}/complete`, { filename: file.name, content_type: file.type });
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setProgress(0);
    try {
      if (file.size > CHUNK_THRESHOLD) {
        await uploadChunked(file);
      } else {
        await uploadSingle(file);
      }
      toast.success(t("media.toast.uploaded", { name: file.name }));
      await load();
    } catch (e) {
      toast.error(extractApiError(e, t("media.toast.uploadFailed"), tv));
    } finally {
      setBusy(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (it) => {
    if (!window.confirm(t("media.confirm.delete"))) return;
    try {
      await api.delete(`/admin/media/${it.id}`);
      toast.success(t("media.toast.deleted"));
      await load();
    } catch (e) {
      // Without this the file silently stayed in the library — and the failure was an
      // unhandled rejection, so not even the console said so.
      toast.error(extractApiError(e, t("toast.deleteFailed"), tv));
    }
  };

  const fullUrl = (path) => (path.startsWith("http") ? path : `${process.env.REACT_APP_BACKEND_URL}${path}`);

  const copy = (it) => {
    navigator.clipboard.writeText(fullUrl(it.url));
    setCopiedId(it.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <div data-testid="admin-media">
      <h1 className="font-display text-4xl text-eh-ink">{t("nav.media")}</h1>
      <p className="text-eh-muted mt-1">{t("media.subtitle")}</p>

      <div
        className="mt-8 bg-eh-cream border-2 border-dashed border-eh-border rounded-3xl p-8 text-center"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); e.dataTransfer.files?.[0] && upload(e.dataTransfer.files[0]); }}
      >
        <Upload size={28} className="mx-auto text-eh-sage-deep" />
        <p className="mt-3 text-eh-ink">{t("media.dropzone.text")}</p>
        <p className="text-xs text-eh-muted mt-1">{t("media.dropzone.limits")}</p>
        <label className="inline-block mt-4 btn-primary text-sm cursor-pointer" data-testid="admin-media-upload-label">
          {busy ? <Loader2 size={14} className="animate-spin inline" /> : <Upload size={14} className="inline" />} {busy ? t("media.uploading", { n: progress }) : t("media.upload")}
          <input
            ref={fileRef}
            type="file"
            accept="image/*,video/*"
            disabled={busy}
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
            className="hidden"
            data-testid="admin-media-upload"
          />
        </label>

        {busy && (
          <div className="mt-4 max-w-sm mx-auto" data-testid="admin-media-progress">
            <div className="h-2 bg-eh-bg rounded-full overflow-hidden">
              <div className="h-full bg-eh-sage-deep transition-all duration-200" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Import depuis Google Drive (Picker officiel) ou un lien */}
      <div className="mt-6 bg-white border border-eh-border-soft rounded-2xl p-5" data-testid="admin-media-import">
        <div className="flex items-center gap-2 text-eh-ink">
          <Link2 size={16} className="text-eh-sage-deep" />
          <span className="font-medium text-sm">{t("media.import.title")}</span>
        </div>
        <p className="text-xs text-eh-muted mt-1">
          {t("media.import.subtitle")}
        </p>

        {driveConfigured && (
          <div className="mt-3">
            <button
              onClick={importFromDrive}
              disabled={!driveReady || importing}
              data-testid="admin-media-drive-btn"
              className="inline-flex items-center justify-center gap-2 text-sm bg-white border border-eh-border-soft rounded-full px-5 py-2 hover:bg-eh-cream transition disabled:opacity-50"
            >
              {importing ? <Loader2 size={14} className="animate-spin" /> : (
                <svg viewBox="0 0 87.3 78" width="16" height="16" aria-hidden="true">
                  <path fill="#0066da" d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5z" />
                  <path fill="#00ac47" d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L1.2 48.5C.4 49.9 0 51.45 0 53h27.5z" />
                  <path fill="#ea4335" d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.798l5.852 11.5z" />
                  <path fill="#00832d" d="M43.65 25L57.4 1.2C56.05.4 54.5 0 52.9 0H34.4c-1.6 0-3.15.45-4.5 1.2z" />
                  <path fill="#2684fc" d="M59.8 53H27.5L13.75 76.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" />
                  <path fill="#ffba00" d="M73.4 26.5l-12.65-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25 59.8 53h27.45c0-1.55-.4-3.1-1.2-4.5z" />
                </svg>
              )}
              {driveReady ? t("media.import.drivePick") : t("media.import.driveLoading")}
            </button>
          </div>
        )}

        <div className="mt-3 flex flex-col sm:flex-row gap-2 items-start">
          <div className="flex-1 w-full">
            <input
              type="url"
              value={importUrl}
              onChange={(e) => { setImportUrl(e.target.value); clearFieldError("importUrl"); }}
              onKeyDown={(e) => e.key === "Enter" && !importing && importFromUrl()}
              placeholder={t("media.import.placeholder")}
              disabled={importing}
              aria-invalid={fieldErrors.importUrl ? true : undefined}
              data-testid="admin-media-import-input"
              className={cn(
                "w-full text-sm border border-eh-border-soft rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-eh-sage/40",
                fieldErrors.importUrl && invalidInputClass,
              )}
            />
            <FieldError
              message={fieldErrors.importUrl ? tv(fieldErrors.importUrl) : ""}
              testId="admin-media-import-input-error"
              className="ml-4"
            />
          </div>
          <button
            onClick={importFromUrl}
            disabled={importing}
            data-testid="admin-media-import-btn"
            className="inline-flex items-center justify-center gap-1.5 text-sm bg-eh-ink text-eh-bg rounded-full px-5 py-2 hover:opacity-90 transition disabled:opacity-50"
          >
            {importing ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} {t("media.import.submit")}
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((it) => (
          <div key={it.id} data-testid={`media-card-${it.id}`} className="group relative bg-eh-cream border border-eh-border-soft rounded-2xl overflow-hidden">
            <div className="aspect-square bg-black/5 flex items-center justify-center">
              {it.kind === "video" ? (
                <video src={fullUrl(it.url)} className="w-full h-full object-cover" controls preload="metadata" />
              ) : (
                <img src={fullUrl(it.url)} alt={it.original_name} className="w-full h-full object-cover" loading="lazy" />
              )}
            </div>
            <div className="p-3">
              <div className="text-xs text-eh-ink truncate flex items-center gap-1" title={it.original_name}>
                {it.kind === "video" && <Film size={11} className="shrink-0 text-eh-muted" />}
                {it.original_name}
              </div>
              <div className="text-[10px] text-eh-muted">{(it.size / (1024 * 1024) >= 1) ? t("media.size.mb", { n: (it.size / (1024 * 1024)).toFixed(1) }) : t("media.size.kb", { n: Math.round(it.size / 1024) })}</div>
              <div className="mt-2 flex items-center gap-1">
                <button onClick={() => copy(it)} data-testid={`media-copy-${it.id}`} className="flex-1 text-[10px] inline-flex items-center justify-center gap-1 bg-white border border-eh-border-soft rounded-full px-2 py-1 hover:bg-eh-bg">
                  {copiedId === it.id ? <><Check size={10} /> {t("media.copied")}</> : <><Copy size={10} /> {t("media.copyUrl")}</>}
                </button>
                <button onClick={() => remove(it)} data-testid={`media-delete-${it.id}`} className="p-1.5 rounded-full text-red-600 hover:bg-red-50">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
